import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Send, Error as ErrorIcon } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import Tabs from "../../../elements/v2/Tabs/Tabs";
import { useStore } from "../../../hooks";
import { useBlastCampaign } from "./context";
import { CONTACT_SOURCES } from "./constants/blastConstants";
import {
  ContactSourceSelector,
  ContactsUpload,
  ContactsFromDatabase,
  FieldMapper,
  ContactsPreview,
  EmailTemplateTab,
  BlastResults,
} from "./components";
import {
  PageContainer,
  Header,
  HeaderLeft,
  PageTitle,
  PageSubtitle,
  HeaderRight,
  BlastButton,
  Content,
  TabsContainer,
  TabContent,
  ErrorBanner,
  LoadingOverlay,
} from "./BlastCampaignPage.styled";

const BlastCampaignPage = observer(() => {
  const { authStore, changeTitle } = useStore();
  const {
    contactSource,
    blastContacts,
    isLoading,
    isReadyToBlast,
    error,
    setTenantId,
    clearError,
    blast,
  } = useBlastCampaign();

  const [activeTab, setActiveTab] = useState(0);

  // Set page title
  useEffect(() => {
    changeTitle?.("Blast Campaign");
  }, [changeTitle]);

  // Initialize tenant ID
  useEffect(() => {
    if (authStore.currentWorkspaceUuid) {
      setTenantId(authStore.currentWorkspaceUuid);
    }
  }, [authStore.currentWorkspaceUuid, setTenantId]);

  const handleBlast = async () => {
    const result = await blast();
    if (result.success) {
      // Results modal will show automatically via BlastResults component
    }
  };

  const handleContactsConfirmed = () => {
    // Move to email template tab
    setActiveTab(1);
  };

  // Build tabs based on contact source
  const tabs = [
    {
      label: "Contacts",
      content: (
        <TabContent>
          <ContactSourceSelector />
          
          {contactSource === CONTACT_SOURCES.CSV ? (
            <>
              <ContactsUpload />
              <FieldMapper onSave={handleContactsConfirmed} />
            </>
          ) : (
            <ContactsFromDatabase onConfirm={handleContactsConfirmed} />
          )}

          <ContactsPreview />
        </TabContent>
      ),
    },
    {
      label: "Email Template",
      content: (
        <TabContent>
          <EmailTemplateTab />
        </TabContent>
      ),
    },
  ];

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <PageTitle>Blast Campaign</PageTitle>
          <PageSubtitle>
            Send emails to multiple contacts at once
          </PageSubtitle>
        </HeaderLeft>

        <HeaderRight>
          <BlastButton
            onClick={handleBlast}
            disabled={!isReadyToBlast || isLoading}
            aria-label="Send blast campaign"
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Send />
            )}
            {isLoading ? "Sending..." : `Send Blast (${blastContacts.length})`}
          </BlastButton>
        </HeaderRight>
      </Header>

      {error && (
        <ErrorBanner role="alert">
          <ErrorIcon />
          <span>{error}</span>
          <button type="button" onClick={clearError}>
            Dismiss
          </button>
        </ErrorBanner>
      )}

      <Content>
        <TabsContainer>
          <Tabs
            tabs={tabs}
            setTab={activeTab}
            onToggleTab={setActiveTab}
          />
        </TabsContainer>
      </Content>

      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={48} />
        </LoadingOverlay>
      )}

      <BlastResults onNavigateToContacts={() => setActiveTab(0)} />
    </PageContainer>
  );
});

export default BlastCampaignPage;
